import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Navigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { UserContext } from "context/UserContext";
import axiosInstance from "services/axiosConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BaseEnum from "enums/BaseEnum";
import PropTypes from "prop-types";

const roleLabels = {
  ROLE_MANAGER: BaseEnum.ROLE_MANAGER,
  ROLE_SALES_RESPONSIBLE: BaseEnum.ROLE_SALES_RESPONSIBLE,
  ROLE_RESOURCE: BaseEnum.ROLE_RESOURCE,
  ROLE_ACCOUNTANT: BaseEnum.ROLE_ACCOUNTANT,
  ROLE_LOGISTICS_OFFICER: BaseEnum.ROLE_LOGISTICS_OFFICER,
  ROLE_CUSTOMER: "Müşteri",
};

function InfoItem({ label, value }) {
  return (
    <Grid item xs={12} md={6}>
      <MDTypography variant="button" color="text">
        <strong>{label}:</strong> {value || "-"}
      </MDTypography>
    </Grid>
  );
}
InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function Users() {
  const { user, loading: userLoading } = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const hasAccess = user?.roles?.includes("ROLE_MANAGER");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("users/employees");
      const users = response.data || [];
      setRows(
        users.map((item) => ({
          id: item.id,
          username: item.username,
          firstName: item.firstName || "-",
          lastName: item.lastName || "-",
          email: item.email || "-",
          phone: item.phone || "-",
          roles: (item.roles || []).map((role) => roleLabels[role] || role).join(", "),
          rawRoles: item.roles || [],
          createdBy: item.createdBy || "-",
          uniqueId: item.uniqueId || "-",
        }))
      );
    } catch (error) {
      console.error("Users: fetch error", error);
      toast.error("Kullanıcı listesi alınırken hata oluştu", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userLoading && hasAccess) {
      fetchUsers();
    }
  }, [userLoading, hasAccess]);

  const handleRowClick = (params) => {
    setSelectedUser(params.row);
    setDetailOpen(true);
  };

  const columns = [
    { field: "username", headerName: "Kullanıcı Adı", flex: 1 },
    { field: "firstName", headerName: BaseEnum.NAME, flex: 1 },
    { field: "lastName", headerName: "Soyisim", flex: 1 },
    { field: "email", headerName: BaseEnum.EMAIL, flex: 1.2 },
    { field: "phone", headerName: BaseEnum.PHONE, flex: 1 },
    { field: "roles", headerName: "Roller", flex: 1.5 },
  ];

  if (!userLoading && !hasAccess) {
    return <Navigate to="/orders/create" replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium">
                  Çalışanlar
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Sistemdeki tüm kullanıcılar ve rollerini görüntüleyebilirsiniz.
                </MDTypography>
              </MDBox>
              <MDBox p={3} pt={0}>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  onRowClick={handleRowClick}
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 300 },
                    },
                  }}
                  localeText={{
                    toolbarColumns: "Sütunlar",
                    toolbarFilters: "Filtreler",
                    toolbarExport: "Dışa Aktar",
                    toolbarDensity: "Yoğunluk",
                    toolbarQuickFilterPlaceholder: "Ara...",
                    noRowsLabel: "Listelenecek kullanıcı bulunamadı",
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{selectedUser?.username}</DialogTitle>
          <DialogContent dividers>
            {selectedUser ? (
              <>
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  Kullanıcı Bilgileri
                </MDTypography>
                <Grid container spacing={2}>
                  <InfoItem label={BaseEnum.NAME} value={selectedUser.firstName} />
                  <InfoItem label="Soyisim" value={selectedUser.lastName} />
                  <InfoItem label={BaseEnum.EMAIL} value={selectedUser.email} />
                  <InfoItem label={BaseEnum.PHONE} value={selectedUser.phone} />
                  <InfoItem label="Roller" value={selectedUser.roles} />
                </Grid>
                <Divider sx={{ my: 2 }} />
                <MDTypography variant="body2" color="text">
                  Kullanıcıyla ilgili detaylı işlemleri yönetici panelinden gerçekleştirebilirsiniz.
                </MDTypography>
              </>
            ) : (
              <MDTypography variant="body2" color="text">
                Kullanıcı bilgisi yüklenemedi.
              </MDTypography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Kapat</Button>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default Users;
